import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";

import * as contractsApi from "@/api/marketplace_contracts";
import * as pickersApi from "@/api/one_c_pickers";
import * as connectionsApi from "@/api/connections";
import * as returnsApi from "@/api/returns";
import {
  PickerRow,
  RemotePicker,
} from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const MARKETPLACES = returnsApi.MARKETPLACE_ORDER.map((v) => ({
  value: v,
  label: returnsApi.MARKETPLACE_TITLES[v],
}));

/** Возвраты этого маркетплейса идут перемещением, а не по комиссии. */
function isTransfer(mp: string): boolean {
  return returnsApi.RETURN_KINDS[mp as returnsApi.Marketplace] === "transfer";
}

interface FormState {
  connectionId: string;
  marketplace: string;
  organization: PickerRow | null;
  kontragent: PickerRow | null;
  dogovor: PickerRow | null;
  warehouse: PickerRow | null;
  memoRecipientRole: string;
  memoRecipientName: string;
}

const EMPTY: FormState = {
  connectionId: "",
  marketplace: "wb",
  organization: null,
  kontragent: null,
  dogovor: null,
  warehouse: null,
  memoRecipientRole: "Генеральному директору",
  memoRecipientName: "",
};

const RECIPIENT_ROLES = [
  "Генеральному директору",
  "Индивидуальному предпринимателю",
];

export function MarketplaceContractsCard(): React.ReactElement {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: connections = [] } = useQuery({
    queryKey: ["connections"],
    queryFn: connectionsApi.listConnections,
  });

  // Default form connection_id to the first available connection once loaded.
  React.useEffect(() => {
    if (!form.connectionId && connections.length > 0) {
      const defaultConn = connections.find((c) => c.is_default) ?? connections[0];
      if (defaultConn) {
        setForm((s) => ({ ...s, connectionId: defaultConn.id }));
      }
    }
  }, [connections, form.connectionId]);

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ["marketplace-contracts"],
    queryFn: () => contractsApi.listContracts(),
  });

  const createMut = useMutation({
    mutationFn: contractsApi.createContract,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketplace-contracts"] });
      resetForm();
      toast.success("Маппинг создан");
    },
    onError: (e: { response?: { data?: { detail?: string } } }) =>
      toast.error(e.response?.data?.detail ?? "Ошибка создания"),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: contractsApi.ContractUpdate }) =>
      contractsApi.updateContract(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketplace-contracts"] });
      resetForm();
      toast.success("Маппинг обновлён");
    },
    onError: (e: { response?: { data?: { detail?: string } } }) =>
      toast.error(e.response?.data?.detail ?? "Ошибка обновления"),
  });

  const deleteMut = useMutation({
    mutationFn: contractsApi.deleteContract,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketplace-contracts"] });
      toast.success("Удалено");
    },
  });

  const resetForm = () => {
    setEditingId(null);
    setForm((s) => ({ ...EMPTY, connectionId: s.connectionId, marketplace: s.marketplace }));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // ⚠️ Требуем РАЗНОЕ в зависимости от вида возврата: у Яндекса контрагента
    // и договора не существует, и просить их значило бы просить выдумать.
    const transfer = isTransfer(form.marketplace);
    if (!form.connectionId || !form.organization) {
      toast.error("Заполните все поля");
      return;
    }
    if (transfer ? !form.warehouse : !form.kontragent || !form.dogovor) {
      toast.error(
        transfer
          ? "Выберите склад, с которого возвращаем"
          : "Выберите контрагента и договор",
      );
      return;
    }
    if (editingId) {
      updateMut.mutate({
        id: editingId,
        body: {
          organization_name: form.organization.name,
          kontragent_ref: form.kontragent?.ref ?? null,
          kontragent_name: form.kontragent?.name ?? null,
          dogovor_ref: form.dogovor?.ref ?? null,
          dogovor_name: form.dogovor?.name ?? null,
          warehouse_ref: form.warehouse?.ref ?? null,
          warehouse_name: form.warehouse?.name ?? null,
          memo_recipient_role: form.memoRecipientRole || null,
          memo_recipient_name: form.memoRecipientName || null,
        },
      });
    } else {
      createMut.mutate({
        connection_id: form.connectionId,
        marketplace: form.marketplace,
        organization_ref: form.organization.ref,
        organization_name: form.organization.name,
        kontragent_ref: form.kontragent?.ref ?? null,
        kontragent_name: form.kontragent?.name ?? null,
        dogovor_ref: form.dogovor?.ref ?? null,
        dogovor_name: form.dogovor?.name ?? null,
        warehouse_ref: form.warehouse?.ref ?? null,
        warehouse_name: form.warehouse?.name ?? null,
        memo_recipient_role: form.memoRecipientRole || null,
        memo_recipient_name: form.memoRecipientName || null,
      });
    }
  };

  const startEdit = (c: contractsApi.ContractView) => {
    setEditingId(c.id);
    setForm({
      connectionId: c.connection_id,
      marketplace: c.marketplace,
      organization: { ref: c.organization_ref, name: c.organization_name },
      kontragent: c.kontragent_ref
        ? { ref: c.kontragent_ref, name: c.kontragent_name ?? "" }
        : null,
      dogovor: c.dogovor_ref
        ? { ref: c.dogovor_ref, name: c.dogovor_name ?? "" }
        : null,
      warehouse: c.warehouse_ref
        ? { ref: c.warehouse_ref, name: c.warehouse_name ?? "" }
        : null,
      memoRecipientRole: c.memo_recipient_role ?? "Генеральному директору",
      memoRecipientName: c.memo_recipient_name ?? "",
    });
  };

  const connId = form.connectionId || undefined;
  const kontragentRef = form.kontragent?.ref;
  const organizationRef = form.organization?.ref;
  const [allDogovorTypes, setAllDogovorTypes] = useState(false);

  const dogovorSearch = useMemo(() => {
    if (!kontragentRef) return null;
    return (q: string) =>
      pickersApi.searchDogovors(kontragentRef, q, {
        connectionId: connId,
        organizationRef,
        // Default — только «СКомиссионером»; чекбокс «Все типы» снимает фильтр.
        vidDogovora: allDogovorTypes ? "" : undefined,
      });
  }, [kontragentRef, connId, organizationRef, allDogovorTypes]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Договоры комиссии маркетплейсов</h2>
        <p className="text-sm text-muted-foreground">
          Связка «маркетплейс × организация → контрагент» нужна для чтения остатков
          переданных товаров комиссионеру и для создания «возврата от комиссионера»
          в 1С УНФ.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {editingId ? "Редактирование маппинга" : "Новый маппинг"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Подключение 1С</Label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.connectionId}
                  onChange={(e) =>
                    // Picker-ы держат GUID-ы конкретной 1С-базы.
                    // При смене подключения они становятся stale → обнуляем.
                    setForm({
                      ...form,
                      connectionId: e.target.value,
                      organization: null,
                      kontragent: null,
                      dogovor: null,
                    })
                  }
                  disabled={!!editingId}
                  required
                >
                  {connections.length === 0 && <option value="">Нет подключений</option>}
                  {connections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Маркетплейс</Label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.marketplace}
                  onChange={(e) => setForm({ ...form, marketplace: e.target.value })}
                  disabled={!!editingId}
                  required
                >
                  {MARKETPLACES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Организация</Label>
              <RemotePicker<pickersApi.OrganizationPickerRow>
                value={
                  form.organization
                    ? {
                        ref: form.organization.ref,
                        name: form.organization.name,
                        full_name: form.organization.name,
                        legal_form: "",
                      }
                    : null
                }
                onChange={(r) => {
                  if (!r) {
                    setForm({ ...form, organization: null });
                    return;
                  }
                  // Подменяем `name` на «НаименованиеПолное» — это то, что
                  // должно отображаться в шапке СЗ. Автоматически выставляем
                  // обращение по ЮЛ/ИП.
                  const fullName = r.full_name || r.name;
                  const role =
                    r.legal_form === "ФизическоеЛицо"
                      ? "Индивидуальному предпринимателю"
                      : r.legal_form === "ЮридическоеЛицо"
                        ? "Генеральному директору"
                        : form.memoRecipientRole;
                  setForm({
                    ...form,
                    organization: { ref: r.ref, name: fullName },
                    memoRecipientRole: role,
                  });
                }}
                search={(q) => pickersApi.searchOrganizations(q, connId)}
                queryKey={["one-c-organizations", connId]}
                placeholder="Начните вводить название организации…"
                disabled={!form.connectionId || !!editingId}
              />
            </div>

            {/* ⚠️ Что показываем — решает ВИД возврата. У `transfer` (Яндекс)
                контрагента и договора не существует: товар уехал перемещением
                на наш же склад и всё время оставался нашим. Показать там поля
                комиссии значило бы просить выдумать реквизиты. */}
            {isTransfer(form.marketplace) ? (
              <div className="space-y-2">
                <Label>Склад, с которого возвращаем</Label>
                <RemotePicker
                  value={form.warehouse}
                  onChange={(r) => setForm({ ...form, warehouse: r })}
                  search={(q) => pickersApi.searchStructuralUnits("Склад", q)}
                  queryKey={["mc-warehouses", form.connectionId]}
                  placeholder="Яндекс.Маркет (склад)"
                />
                <p className="text-xs text-muted-foreground">
                  Товар туда уезжает перемещением и остаётся нашим, поэтому
                  контрагента и договора у такого возврата нет — возврат
                  оформляется обратным перемещением с этого склада.
                </p>
              </div>
            ) : (
              <>
            <div className="space-y-2">
              <Label>Контрагент (маркетплейс)</Label>
              <RemotePicker
                value={form.kontragent}
                onChange={(r) =>
                  setForm({ ...form, kontragent: r, dogovor: null })
                }
                search={(q) => pickersApi.searchKontragents(q, connId)}
                queryKey={["one-c-kontragents", connId]}
                placeholder="Начните вводить название контрагента…"
                disabled={!form.connectionId}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Договор контрагента</Label>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={allDogovorTypes}
                    onChange={(e) => {
                      setAllDogovorTypes(e.target.checked);
                      // Сбрасываем выбранный договор при переключении —
                      // фильтр изменился, прошлый выбор может быть некорректен.
                      setForm((s) => ({ ...s, dogovor: null }));
                    }}
                  />
                  Все типы договоров
                </label>
              </div>
              <RemotePicker
                value={form.dogovor}
                onChange={(r) => setForm({ ...form, dogovor: r })}
                search={dogovorSearch ?? (() => Promise.resolve([]))}
                queryKey={[
                  "one-c-dogovors",
                  connId,
                  kontragentRef,
                  organizationRef,
                  allDogovorTypes ? "all" : "komissioner",
                ]}
                placeholder={
                  !kontragentRef
                    ? "Выберите сначала контрагента"
                    : allDogovorTypes
                      ? "Договор контрагента (все типы)"
                      : "Договор с комиссионером…"
                }
                disabled={!kontragentRef}
              />
              {!organizationRef && kontragentRef && (
                <p className="text-xs text-muted-foreground">
                  Совет: выберите организацию первой — список договоров сузится.
                </p>
              )}
            </div>

              </>
            )}

            <div className="grid gap-3 border-t pt-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Шапка СЗ — обращение</Label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.memoRecipientRole}
                  onChange={(e) =>
                    setForm({ ...form, memoRecipientRole: e.target.value })
                  }
                >
                  {RECIPIENT_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Шапка СЗ — ФИО получателя</Label>
                <input
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  placeholder="Например: Иванову И. И."
                  value={form.memoRecipientName}
                  onChange={(e) =>
                    setForm({ ...form, memoRecipientName: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={createMut.isPending || updateMut.isPending}>
                {editingId ? "Сохранить" : "Создать"}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={resetForm}>
                  Отмена
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Существующие маппинги</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {!isLoading && contracts.length === 0 && (
          <p className="text-sm text-muted-foreground">Маппингов ещё нет.</p>
        )}
        <ul className="space-y-2">
          {contracts.map((c) => (
            <li key={c.id} className="rounded-md border p-3 text-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 font-medium">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs uppercase">
                      {c.marketplace}
                    </span>
                    <span className="truncate">{c.organization_name}</span>
                  </div>
                  {/* У `transfer`-возврата контрагента и договора нет —
                      показываем склад, иначе строка выглядела бы недозаполненной. */}
                  {c.warehouse_ref ? (
                    <div className="text-xs text-muted-foreground">
                      Возврат перемещением со склада:{" "}
                      <span className="text-foreground">{c.warehouse_name}</span>
                    </div>
                  ) : (
                    <>
                  <div className="text-xs text-muted-foreground">
                    Контрагент: <span className="text-foreground">{c.kontragent_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Договор: <span className="text-foreground">{c.dogovor_name}</span>
                  </div>
                    </>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Удалить маппинг «${c.marketplace} × ${c.organization_name}»?`,
                        )
                      ) {
                        deleteMut.mutate(c.id);
                      }
                    }}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
